import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import { Mail, Plus, Trash2, Send, Pencil, Clock, Calendar } from 'lucide-react';

const TIPOS_LABEL = {
  resumen_ejecutivo: 'Resumen ejecutivo',
  horas_extra: 'Horas extra',
  absentismo: 'Absentismo',
  cumplimiento_legal: 'Cumplimiento legal',
  nocturnidad: 'Nocturnidad (art. 36 ET)',
  productividad_depts: 'Productividad por dep.',
  detallado: 'Detallado (solo Excel)',
};

const DIAS_SEMANA = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const nuevoForm = () => ({
  nombre: '',
  tipo: 'resumen_ejecutivo',
  periodicidad: 'mensual',
  dia_envio: 1,
  hora_envio: 8,
  formatos: ['pdf'],
  destinatarios: [],
  incluir_todos_admins: true,
  activo: true,
});

const InformesProgramadosCard = () => {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(nuevoForm());
  const [editingId, setEditingId] = useState(null);
  const [destInput, setDestInput] = useState('');

  const cargar = () => api.get('/admin/informes-programados').then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { cargar(); }, []);

  const abrirNuevo = () => {
    setForm(nuevoForm()); setEditingId(null); setDestInput(''); setOpen(true);
  };
  const abrirEditar = (p) => {
    setForm({
      nombre: p.nombre, tipo: p.tipo, periodicidad: p.periodicidad,
      dia_envio: p.dia_envio, hora_envio: p.hora_envio, formatos: p.formatos || ['pdf'],
      destinatarios: p.destinatarios || [], incluir_todos_admins: !!p.incluir_todos_admins,
      activo: !!p.activo,
    });
    setEditingId(p.id); setDestInput(''); setOpen(true);
  };

  const agregarDest = () => {
    const v = destInput.trim();
    if (!v) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) { toast.error('Email no válido'); return; }
    if (form.destinatarios.includes(v)) return;
    setForm((f) => ({ ...f, destinatarios: [...f.destinatarios, v] }));
    setDestInput('');
  };
  const quitarDest = (e) => setForm((f) => ({ ...f, destinatarios: f.destinatarios.filter((x) => x !== e) }));

  const toggleFormato = (fmt) => setForm((f) => ({
    ...f,
    formatos: f.formatos.includes(fmt) ? f.formatos.filter((x) => x !== fmt) : [...f.formatos, fmt],
  }));

  const guardar = async () => {
    if (!form.nombre.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (form.formatos.length === 0) { toast.error('Selecciona al menos un formato (PDF/Excel)'); return; }
    if (!form.incluir_todos_admins && form.destinatarios.length === 0) {
      toast.error('Añade al menos un destinatario o marca "Incluir a todos los admins"');
      return;
    }
    try {
      if (editingId) {
        await api.put(`/admin/informes-programados/${editingId}`, form);
        toast.success('Informe programado actualizado');
      } else {
        await api.post('/admin/informes-programados', form);
        toast.success('Informe programado creado');
      }
      setOpen(false); cargar();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    }
  };

  const eliminar = async (p) => {
    try { await api.delete(`/admin/informes-programados/${p.id}`); cargar(); toast.success('Eliminado'); }
    catch { toast.error('Error al eliminar'); }
  };
  const toggleActivo = async (p) => {
    try { await api.put(`/admin/informes-programados/${p.id}`, { activo: !p.activo }); cargar(); }
    catch { toast.error('Error'); }
  };
  const enviarAhora = async (p) => {
    try {
      const r = await api.post(`/admin/informes-programados/${p.id}/enviar`);
      if (r.data?.enviados) toast.success(`Enviado a ${r.data.enviados} destinatarios`);
      else toast.info('Sin destinatarios o sin datos para el periodo');
      cargar();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar'); }
  };

  const descCuando = (p) => {
    if (p.periodicidad === 'semanal') return `Semanal — ${DIAS_SEMANA[p.dia_envio - 1]} a las ${String(p.hora_envio).padStart(2, '0')}:00`;
    return `Mensual — día ${p.dia_envio} a las ${String(p.hora_envio).padStart(2, '0')}:00`;
  };

  return (
    <Card className="border-zinc-200" data-testid="informes-programados-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
        <div>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Mail className="h-5 w-5" /> Informes programados por email
          </CardTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Configura envíos automáticos de informes profesionales. Se generan y envían por email según la periodicidad elegida.
          </p>
        </div>
        <Button onClick={abrirNuevo} className="bg-zinc-900 hover:bg-zinc-800" data-testid="nuevo-programado-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Nuevo
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        {list.length === 0 ? (
          <div className="py-8 text-center text-zinc-400 text-sm">
            Aún no has configurado ningún envío automático. Pulsa &quot;Nuevo&quot; para crear el primero.
          </div>
        ) : (
          <div className="divide-y divide-zinc-100">
            {list.map((p) => (
              <div key={p.id} className="p-4 flex flex-wrap items-center gap-3" data-testid={`programado-row-${p.id}`}>
                <Switch checked={!!p.activo} onCheckedChange={() => toggleActivo(p)} data-testid={`programado-toggle-${p.id}`} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-zinc-900 truncate">{p.nombre}</div>
                    <Badge variant="outline" className="text-xs">{TIPOS_LABEL[p.tipo] || p.tipo}</Badge>
                    {(p.formatos || []).map((f) => (
                      <Badge key={f} className="text-xs bg-zinc-100 text-zinc-700">{f.toUpperCase()}</Badge>
                    ))}
                  </div>
                  <div className="text-xs text-zinc-500 mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {descCuando(p)}</span>
                    <span className="inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {p.incluir_todos_admins ? 'Todos los admins' : ''}
                      {p.incluir_todos_admins && (p.destinatarios || []).length > 0 ? ' + ' : ''}
                      {(p.destinatarios || []).slice(0, 2).join(', ')}
                      {(p.destinatarios || []).length > 2 ? ` +${p.destinatarios.length - 2}` : ''}
                    </span>
                    {p.ultimo_envio && (
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" /> Último: {new Date(p.ultimo_envio).toLocaleString('es-ES', { timeZone: 'Europe/Madrid' })}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => enviarAhora(p)} data-testid={`enviar-ahora-${p.id}`} title="Enviar ahora">
                    <Send className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => abrirEditar(p)} data-testid={`editar-programado-${p.id}`} title="Editar">
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-red-600" data-testid={`eliminar-programado-${p.id}`} title="Eliminar">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar &quot;{p.nombre}&quot;?</AlertDialogTitle>
                        <AlertDialogDescription>Dejará de enviarse automáticamente. Esta acción no se puede deshacer.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => eliminar(p)}>Eliminar</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="programado-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Editar informe programado' : 'Nuevo informe programado'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Nombre</Label>
              <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder='Ej: "Resumen mensual para dirección"' data-testid="prog-nombre" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Tipo de informe</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="prog-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(TIPOS_LABEL).filter(([k]) => k !== 'detallado').map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Periodicidad</Label>
                <Select value={form.periodicidad} onValueChange={(v) => setForm({ ...form, periodicidad: v, dia_envio: 1 })}>
                  <SelectTrigger data-testid="prog-periodicidad"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="semanal">Semanal (semana anterior)</SelectItem>
                    <SelectItem value="mensual">Mensual (mes anterior)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>{form.periodicidad === 'semanal' ? 'Día de la semana' : 'Día del mes'}</Label>
                {form.periodicidad === 'semanal' ? (
                  <Select value={String(form.dia_envio)} onValueChange={(v) => setForm({ ...form, dia_envio: parseInt(v, 10) })}>
                    <SelectTrigger data-testid="prog-dia"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DIAS_SEMANA.map((d, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input type="number" min="1" max="28" value={form.dia_envio}
                    onChange={(e) => setForm({ ...form, dia_envio: Math.max(1, Math.min(28, parseInt(e.target.value || '1', 10))) })}
                    data-testid="prog-dia" />
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Hora de envío (0-23)</Label>
                <Input type="number" min="0" max="23" value={form.hora_envio}
                  onChange={(e) => setForm({ ...form, hora_envio: Math.max(0, Math.min(23, parseInt(e.target.value || '0', 10))) })}
                  data-testid="prog-hora" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Formatos</Label>
              <div className="flex items-center gap-4">
                {['pdf', 'excel'].map((fmt) => (
                  <label key={fmt} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={form.formatos.includes(fmt)} onChange={() => toggleFormato(fmt)}
                      data-testid={`prog-formato-${fmt}`} className="accent-zinc-900" />
                    {fmt.toUpperCase()}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Destinatarios (emails)</Label>
              <div className="flex gap-2">
                <Input type="email" placeholder="ceo@empresa.com" value={destInput}
                  onChange={(e) => setDestInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarDest(); } }}
                  data-testid="prog-dest-input" />
                <Button type="button" variant="outline" onClick={agregarDest} data-testid="prog-add-dest">Añadir</Button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {form.destinatarios.map((e) => (
                  <Badge key={e} variant="outline" className="gap-1 pl-2 pr-1 py-0.5">
                    {e}
                    <button type="button" onClick={() => quitarDest(e)} className="hover:text-red-600 ml-0.5">×</button>
                  </Badge>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
                <input type="checkbox" checked={form.incluir_todos_admins}
                  onChange={(e) => setForm({ ...form, incluir_todos_admins: e.target.checked })}
                  data-testid="prog-incluir-admins" className="accent-zinc-900" />
                <span>Incluir automáticamente a todos los admins de la empresa</span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-zinc-900 hover:bg-zinc-800" onClick={guardar} data-testid="prog-guardar">
              {editingId ? 'Guardar cambios' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default InformesProgramadosCard;
